import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, AlertCircle, Paperclip, FileText, Loader2, PenLine, Save, X,
  Trash2, Download, Brain, Sparkles, History,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { API, extractErr } from '../utils';
import { CriteriaEditor } from './CriteriaEditor';

/**
 * Tenant-portal KB card. View + inline edit with AI-draft, preview and
 * regrade. Deliberately lighter than the admin KBCard — tenants can't move
 * KBs across domains, so no reassign/clone controls.
 */
export const KBCard = ({ kb, canWrite, token, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [regrading, setRegrading] = useState(false);
  const [regradeDays, setRegradeDays] = useState(7);
  const [previewResult, setPreviewResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEvents, setHistoryEvents] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const fileInputRef = useRef(null);

  const startEdit = () => {
    setForm({
      name: kb.name || '',
      call_type: kb.call_type || '',
      description: kb.description || '',
      threshold_score: kb.threshold_score ?? 70,
      grading_instructions: kb.grading_instructions || '',
      escalation_emails: kb.escalation_emails || [],
      criteria: (kb.criteria || []).map(c => ({ description: c.description || '', weight: c.weight ?? 1 })),
    });
    setEditing(true);
    setExpanded(true);
    setPreviewResult(null);
  };

  const doSave = async () => {
    if (!form.name.trim() || !form.call_type.trim()) {
      toast.error('Name and call type are required');
      return false;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/knowledge-bases/${kb.id}`, form, { timeout: 30000 });
      toast.success('Knowledge base updated');
      const ar = r?.data?.auto_regrade;
      if (ar?.queued) {
        toast.info(
          `Auto-regrading ${ar.queued} recent call${ar.queued === 1 ? '' : 's'} against the new rule${ar.rules_added === 1 ? '' : 's'} · refresh in a few minutes`,
          { duration: 6000 },
        );
      }
      onChanged();
      return true;
    } catch (err) {
      console.error('KB save failed', err?.response || err);
      toast.error(extractErr(err, 'Failed to save'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (await doSave()) setEditing(false);
  };

  const handleAiDraft = async () => {
    if (drafting) return;
    setDrafting(true);
    try {
      const r = await axios.post(`${API}/knowledge-bases/${kb.id}/draft-instructions`, {}, { timeout: 90000 });
      setForm(f => ({ ...f, grading_instructions: r.data?.grading_instructions || '' }));
      toast.success(`AI draft ready (${r.data?.doc_chars || 0} chars from uploaded docs). Review & save.`);
    } catch (err) {
      console.error('AI draft failed', err?.response || err);
      toast.error(extractErr(err, 'AI draft failed'));
    } finally {
      setDrafting(false);
    }
  };

  const runPreview = async () => {
    if (previewing) return;
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const r = await axios.post(`${API}/knowledge-bases/${kb.id}/regrade-preview?days=${regradeDays}&samples=5`, {}, { timeout: 180000 });
      setPreviewResult(r.data);
      if (!r.data?.samples?.length) toast.info(r.data?.message || 'No calls available to preview.');
    } catch (err) {
      console.error('Preview failed', err?.response || err);
      toast.error(extractErr(err, 'Preview failed'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSaveAndPreview = async () => {
    if (!(await doSave())) return;
    await runPreview();
  };

  useEffect(() => {
    if (!historyOpen || historyEvents !== null) return;
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const r = await axios.get(`${API}/knowledge-bases/${kb.id}/history?limit=50`);
        if (!cancelled) setHistoryEvents(r.data?.events || []);
      } catch (err) {
        console.error('history fetch failed', err?.response || err);
        if (!cancelled) setHistoryEvents([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [historyOpen, historyEvents, kb.id]);

  const handleRegrade = async () => {
    if (regrading) return;
    if (!window.confirm(`Re-grade ALL "${kb.name}" calls from the last ${regradeDays} day(s) using the saved instructions? Existing scores will be reset.`)) return;
    setRegrading(true);
    try {
      const r = await axios.post(`${API}/knowledge-bases/${kb.id}/regrade?days=${regradeDays}`, {}, { timeout: 60000 });
      toast.success(r.data?.message || `Regrading ${r.data?.count || 0} calls…`);
      onChanged();
    } catch (err) {
      console.error('Regrade failed', err?.response || err);
      toast.error(extractErr(err, 'Regrade failed'));
    } finally {
      setRegrading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/knowledge-bases/${kb.id}/upload-doc`, fd, { timeout: 60000 });
      toast.success(`"${file.name}" uploaded`);
      onChanged();
    } catch (err) {
      console.error('KB upload failed', err?.response || err);
      toast.error(extractErr(err, 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteDoc = async (filename) => {
    if (!window.confirm(`Delete "${filename}"? Grading will fall back to the preset criteria for this KB.`)) return;
    try {
      await axios.delete(
        `${API}/knowledge-bases/${kb.id}/documents/${encodeURIComponent(filename)}`,
        { timeout: 30000 },
      );
      toast.success(`"${filename}" deleted`);
      onChanged();
    } catch (err) {
      console.error('KB delete failed', err?.response || err);
      toast.error(extractErr(err, 'Delete failed'));
    }
  };

  const handleDownload = async (filename) => {
    try {
      const r = await fetch(
        `${API}/knowledge-bases/${kb.id}/documents/${encodeURIComponent(filename)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('KB download failed', err);
      toast.error(err?.message || 'Download failed');
    }
  };

  const handleDeleteKB = async () => {
    if (!window.confirm(`Delete the entire "${kb.name}" knowledge base? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/knowledge-bases/${kb.id}`, { timeout: 30000 });
      toast.success(`"${kb.name}" deleted`);
      onChanged();
    } catch (err) {
      console.error('KB delete failed', err?.response || err);
      toast.error(extractErr(err, 'Failed to delete'));
    }
  };

  const hasDoc = (kb.documents?.length || 0) > 0;

  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`kb-card-${kb.call_type}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">{kb.name}</CardTitle>
              <CardDescription className="mt-1">{kb.description}</CardDescription>
              {hasDoc && (
                <p className="text-xs text-teal-700 mt-1 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Grading uses uploaded document as source-of-truth
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canWrite && !editing && (
              <>
                <input
                  ref={fileInputRef} type="file" className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.csv,.md"
                  onChange={handleUpload}
                  data-testid={`kb-upload-input-${kb.call_type}`}
                />
                <Button
                  variant="outline" size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid={`upload-doc-${kb.call_type}`}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Paperclip className="w-4 h-4 mr-1" />}
                  Upload Doc
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={startEdit}
                  data-testid={`edit-kb-${kb.call_type}`}
                >
                  <PenLine className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setHistoryOpen(o => !o)}
                  data-testid={`history-kb-${kb.call_type}`}
                  title="Change history — every edit, AI draft and regrade"
                >
                  <History className="w-4 h-4 mr-1" /> History
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={handleDeleteKB}
                  className="text-red-500 hover:text-red-700 hover:border-red-300"
                  data-testid={`delete-kb-${kb.call_type}`}
                  title="Delete this knowledge base"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
            {!editing && (
              <Button
                variant="outline" size="sm"
                onClick={() => setExpanded(!expanded)}
                data-testid={`expand-kb-${kb.call_type}`}
              >
                {expanded ? 'Hide' : 'View'} Criteria
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!editing ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-sm text-slate-500">Threshold Score</p>
                <p className="text-lg font-semibold text-slate-900">{kb.threshold_score}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Criteria Count</p>
                <p className="text-lg font-semibold text-slate-900">{kb.criteria?.length || 0}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Escalation Emails</p>
                <p className="text-lg font-semibold text-slate-900">{kb.escalation_emails?.length || 0}</p>
              </div>
            </div>

            {expanded && (
              <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Evaluation Criteria</h4>
                  <div className="space-y-2">
                    {kb.criteria?.map((c, i) => (
                      <div key={i} className="flex items-start justify-between p-3 rounded-lg bg-slate-50">
                        <p className="text-sm text-slate-700 flex-1">{c.description}</p>
                        <span className="ml-4 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                          Weight: {c.weight}
                        </span>
                      </div>
                    ))}
                    {(!kb.criteria || kb.criteria.length === 0) && (
                      <p className="text-sm text-slate-400 italic">No preset criteria — grading relies purely on the uploaded document.</p>
                    )}
                  </div>
                </div>

                {hasDoc && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" /> Uploaded Documents
                      <span className="text-xs text-slate-400 font-normal">(overrides preset criteria)</span>
                    </h4>
                    <div className="space-y-2">
                      {kb.documents.map(doc => (
                        <div key={doc.filename} className="flex items-center gap-2 p-2 rounded-lg bg-teal-50 border border-teal-100 text-sm">
                          <FileText className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <span className="text-slate-700 font-medium flex-1">{doc.filename}</span>
                          <span className="text-slate-400 text-xs">{doc.uploaded_at?.split('T')[0]}</span>
                          <button
                            onClick={() => handleDownload(doc.filename)}
                            className="text-teal-600 hover:text-teal-800"
                            data-testid={`kb-doc-download-${kb.call_type}`}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {canWrite && (
                            <button
                              onClick={() => handleDeleteDoc(doc.filename)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`kb-doc-delete-${kb.call_type}`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {kb.escalation_emails?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Escalation Recipients</h4>
                    <div className="flex items-center space-x-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <div className="flex flex-wrap gap-2">
                        {kb.escalation_emails.map(email => (
                          <span key={email} className="px-2 py-1 bg-white rounded text-xs text-slate-700 border border-amber-200">{email}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Name</label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:border-primary/60"
                  data-testid="kb-edit-name"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Threshold Score</label>
                <input
                  type="number" min={0} max={100} value={form.threshold_score}
                  onChange={e => setForm(f => ({ ...f, threshold_score: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:border-primary/60"
                  data-testid="kb-edit-threshold"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Description</label>
              <textarea
                value={form.description} rows={2}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm mt-0.5 focus:outline-none focus:border-primary/60"
                data-testid="kb-edit-description"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-2">
                  Grading Instructions
                  <span className="text-[10px] text-amber-600 uppercase">Primary authority</span>
                </label>
                <button
                  type="button"
                  onClick={handleAiDraft}
                  disabled={drafting}
                  className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 disabled:opacity-50"
                  data-testid={`kb-ai-draft-${kb.call_type}`}
                  title="Read uploaded docs + criteria → auto-generate a first-draft coaching prompt"
                >
                  {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                  {drafting ? 'Drafting…' : 'AI Draft'}
                </button>
              </div>
              <textarea
                value={form.grading_instructions || ''}
                onChange={e => setForm(f => ({ ...f, grading_instructions: e.target.value }))}
                rows={5}
                maxLength={4000}
                placeholder="Free-text prompt read by the AI FIRST — overrides preset criteria and uploaded docs on any conflict."
                className="w-full bg-white border border-amber-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400 resize-y leading-relaxed"
                data-testid={`kb-grading-instructions-${kb.call_type}`}
              />
              <p className="text-slate-400 text-[10px] mt-1">{(form.grading_instructions || '').length}/4000</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide mb-1 block">
                Criteria — fallback when instructions are silent
              </label>
              <CriteriaEditor form={form} setForm={setForm} />
            </div>

            {previewResult && Array.isArray(previewResult.samples) && previewResult.samples.length > 0 && (
              <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2.5" data-testid={`preview-result-${kb.call_type}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-purple-800 text-xs font-semibold uppercase">Preview — {previewResult.samples.filter(s => s.ok).length} samples (not saved)</p>
                    <p className="text-slate-600 text-xs mt-0.5">
                      Current: {previewResult.summary?.current?.analyzed}A / {previewResult.summary?.current?.escalated}E · avg {previewResult.summary?.current?.avg_score ?? '—'}
                      <span className="text-slate-400 mx-2">→</span>
                      Projected: {previewResult.summary?.projected?.analyzed}A / {previewResult.summary?.projected?.escalated}E · avg {previewResult.summary?.projected?.avg_score ?? '—'}
                    </p>
                  </div>
                  <button onClick={() => setPreviewResult(null)} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {previewResult.samples.map(s => {
                    if (!s.ok) return <p key={s.call_id} className="text-red-600 text-[11px]">Error on {s.call_id}: {s.error}</p>;
                    const delta = s.delta;
                    const deltaColor = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-slate-400';
                    const flipped = s.current_status !== s.projected_status;
                    return (
                      <div key={s.call_id} className="grid grid-cols-6 gap-2 items-center text-[11px] text-slate-600">
                        <span className="col-span-2 font-mono text-slate-500 truncate" title={s.call_id}>{s.call_id.slice(0, 18)}…</span>
                        <span className="text-right">{s.current_score}</span>
                        <span className="text-center text-slate-400">→</span>
                        <span className="text-left">{s.projected_score}</span>
                        <span className={`text-right font-medium ${deltaColor}`}>{delta > 0 ? `+${delta}` : delta}{flipped && <span className="ml-1 text-amber-600">*</span>}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 flex-wrap">
              <div className="flex items-center gap-2 text-xs">
                <label className="text-slate-500">Regrade last</label>
                <select
                  value={regradeDays}
                  onChange={e => setRegradeDays(Number(e.target.value))}
                  className="bg-white border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-purple-400"
                  data-testid={`regrade-days-${kb.call_type}`}
                >
                  {[1, 3, 7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
                </select>
                <button
                  onClick={runPreview}
                  disabled={previewing || saving}
                  className="flex items-center gap-1 px-2 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`preview-${kb.call_type}`}
                  title="Grade 5 samples WITHOUT saving to see the score shift"
                >
                  {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                  Preview
                </button>
                <button
                  onClick={handleRegrade}
                  disabled={regrading || saving}
                  className="flex items-center gap-1 px-2 py-1 border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-800 rounded disabled:opacity-50"
                  data-testid={`regrade-${kb.call_type}`}
                  title="Apply saved instructions to ALL calls in the window"
                >
                  {regrading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                  Regrade
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSaveAndPreview}
                  disabled={saving || previewing}
                  className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
                  data-testid={`save-preview-${kb.call_type}`}
                >
                  {saving || previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Save &amp; Preview
                </Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid={`save-kb-${kb.call_type}`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
        {historyOpen && (
          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`history-panel-${kb.call_type}`}>
            <div className="flex items-center justify-between">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                <History className="w-3 h-3" />Change history
              </p>
              <button onClick={() => setHistoryOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
            </div>
            {historyLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-xs"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
            ) : (historyEvents || []).length === 0 ? (
              <p className="text-slate-400 text-xs py-2">No changes recorded yet.</p>
            ) : (
              <div className="space-y-1">
                {(historyEvents || []).map(e => {
                  const badge = { edit: 'bg-teal-100 text-teal-700', regrade: 'bg-purple-100 text-purple-700', ai_draft: 'bg-amber-100 text-amber-700' }[e.event] || 'bg-slate-100 text-slate-600';
                  const detail = e.event === 'regrade'
                    ? `Regraded ${e.meta?.calls_affected ?? 0} calls (last ${e.meta?.days ?? '?'}d)`
                    : e.event === 'ai_draft'
                      ? `AI draft: ${e.meta?.draft_length ?? '?'} chars from ${(e.meta?.doc_chars ?? 0).toLocaleString()} doc chars`
                      : `Edited: ${(e.meta?.changed_fields || []).join(', ') || 'settings'}`;
                  return (
                    <div key={e.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${badge}`}>{e.event.replace('_', ' ')}</span>
                      <span className="text-slate-700 font-medium truncate">{e.actor_name || e.actor_email || 'unknown'}</span>
                      <span className="text-slate-500 truncate flex-1">{detail}</span>
                      <span className="text-slate-400 text-[11px] whitespace-nowrap">{new Date(e.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
